import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Clock, MapPin, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import NewTabHint from "@/components/NewTabHint";
import FaqSection from "@/components/FaqSection";
import { base44 } from "@/api/base44Client";
import OpenTableWidget from "@/components/OpenTableWidget";
import InfoCard from "@/components/InfoCard";

export default function Reservations() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content" className="pt-32 pb-24 px-6" tabIndex="-1" style={{ scrollMarginTop: "80px" }}>
        <div className="max-w-4xl mx-auto">

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-16">
            <p className="font-accent text-[10px] tracking-[0.45em] uppercase text-primary mb-3" lang="it">Prenotazioni</p>
            <h1 className="font-display text-4xl md:text-6xl text-foreground mb-4 leading-tight">
              Reserve a Table at{" "}
              <span className="italic text-primary">Donna Margherita</span>
            </h1>
            <p className="font-body text-sm text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Join us for an authentic Neapolitan dining experience on the Upper East Side. Of the Upper East Side Italian restaurants taking bookings tonight, we are the Neapolitan restaurant with the brick oven, one of the few Upper East Side Neapolitan restaurants in Manhattan, and home to what guests call the best Neapolitan pizza on the Upper East Side. Reservations are recommended; walk-ins are always welcome based on availability.
            </p>
          </motion.div>

          {/* H2: Book Online */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-16 bg-card border border-border/50 shadow-sm p-6 md:p-10 text-center"
          >
            <h2 className="font-display text-3xl text-foreground mb-3">Book Online</h2>
            <p className="font-body text-sm text-muted-foreground mb-8 max-w-xl mx-auto">
              The easiest way to secure your table is through OpenTable. Choose your date, time, and party size instantly.
            </p>
            <OpenTableWidget />
            <a
              href="https://www.opentable.com/r/donna-margherita-new-york"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Reserve a table at Donna Margherita via OpenTable (opens in a new tab)"
              onClick={() => base44.analytics.track({ eventName: "reservation_click", properties: { source: "reservations_page" } })}
              className="inline-flex items-center min-h-[44px] mt-6 font-body text-xs text-primary hover:underline"
            >
              Or reserve directly on OpenTable →
            </a>
          </motion.section>

          {/* H2: Walk-Ins and Group Reservations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-16"
          >
            <h2 className="font-display text-3xl text-foreground mb-6 text-center">Walk-Ins and Group Reservations</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <InfoCard icon={Users} title="Walk-Ins Welcome">
                <p>We always try to accommodate walk-in guests. On busy evenings, wait times may apply. Calling ahead is recommended.</p>
              </InfoCard>
              <InfoCard icon={Phone} title="Groups and Private Events">
                <p>
                  For parties of 6 or more, please call us directly at{" "}
                  <a href="tel:+12127721169" className="text-primary hover:underline">(212) 772-1169</a>{" "}
                  to arrange a group booking.
                </p>
                <p className="text-xs mt-2">Note: 20% gratuity is added to parties of 5 or more.</p>
              </InfoCard>
            </div>
          </motion.section>

          {/* H2: Hours and Location */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="font-display text-3xl text-foreground mb-6 text-center">Hours and Location</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <InfoCard icon={Clock} title="Hours">
                <p>Monday to Saturday: 12:00 PM to 10:00 PM</p>
                <p>Sunday: 12:00 PM to 9:00 PM</p>
              </InfoCard>
              <InfoCard icon={MapPin} title="Address">
                <address className="not-italic block">
                  <span className="block">1304A Second Avenue</span>
                  <span className="block">(Between 68th & 69th Street)</span>
                  <span className="block">New York, NY 10065</span>
                </address>
                <a
                  href="https://maps.google.com/?q=Donna+Margherita+1304A+2nd+Ave+New+York+NY+10065"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center min-h-[44px] mt-1 text-xs text-primary hover:underline"
                >
                  Get Directions →<NewTabHint />
                </a>
              </InfoCard>
            </div>
          </motion.section>

          {/* Secondary CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/menu"
              className="font-body text-sm font-medium rounded-full px-10 py-4 bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-300 shadow-md text-center"
            >
              View Our Menu
            </Link>
            <a
              href="tel:+12127721169"
              className="font-body text-sm font-medium rounded-full px-10 py-4 border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 text-center"
            >
              Call (212) 772-1169
            </a>
          </motion.div>

          <FaqSection path="/reservations" compact />
        </div>
      </main>
      <Footer />
    </div>
  );
}